import convert from 'color-convert';

const KLAROPS_DOMAIN = 'klarops.com';
const KLAROPS_BRAND_CONFIG = {
  favicon: '/klarops/favicon.ico',
  favicon16: '/klarops/favicon-16x16.png',
  favicon32: '/klarops/favicon-32x32.png',
  logoSvg: '/klarops/logo.svg',
  appleTouchIcon: '/klarops/apple-touch-icon.png',
  androidChrome192: '/klarops/android-chrome-192x192.png',
  androidChrome512: '/klarops/android-chrome-512x512.png',
  shortProductName: 'Cognition',
  productName: 'Klarops Cognition',
  companyName: 'Klarops',
  primaryColor: '#18181B',
};

const BETRWORKR_DOMAIN = 'betrworkr.com';
const BETRWORKR_BRAND_CONFIGS = {
  favicon: '/betrworkr/favicon.ico',
  favicon16: '/betrworkr/favicon-16x16.png',
  favicon32: '/betrworkr/favicon-32x32.png',
  logoSvg: '/betrworkr/logo.svg',
  appleTouchIcon: '/betrworkr/apple-touch-icon.png',
  androidChrome192: '/betrworkr/android-chrome-192x192.png',
  androidChrome512: '/betrworkr/android-chrome-512x512.png',
  productName: 'BetrWorkr Cognition',
  shortProductName: 'Cognition',
  companyName: 'BetrWorkr',
  primaryColor: '#4C9BF4',
};

export const hostname = window.location.hostname;

const isKlarops = hostname.endsWith(KLAROPS_DOMAIN);
// const isBetrWorkr = true;
const isBetrWorkr = hostname.endsWith(BETRWORKR_DOMAIN);

type BrandKey = 'klarops' | 'betrworkr';

const brands: Record<BrandKey, boolean> = {
  klarops: isKlarops,
  betrworkr: isBetrWorkr,
};

export const getCurrentBrand = (): BrandKey => {
  const foundBrand = (Object.keys(brands) as BrandKey[]).find((brand) => brands[brand]);
  return foundBrand ?? 'klarops';
};

type BrandConfig = {
  favicon: string;
  favicon16: string;
  favicon32: string;
  logoSvg: string;
  appleTouchIcon: string;
  androidChrome192: string;
  androidChrome512: string;
  productName: string;
  shortProductName: string;
  companyName: string;
  primaryColor: string;
};

const brandConfigs: Record<BrandKey, BrandConfig> = {
  klarops: KLAROPS_BRAND_CONFIG,
  betrworkr: BETRWORKR_BRAND_CONFIGS,
};

export const brandedConfig = brandConfigs[getCurrentBrand()];

export const setBrandColor = (brandColor = KLAROPS_BRAND_CONFIG.primaryColor) => {
  const hslValue = convert.hex.hsl(brandColor).join(' ');
  document.documentElement.style.setProperty('--primary', hslValue);
};

export const setupDocumentHead = () => {
  document.title = brandedConfig.productName;
  const head = document.head;
  const existingLinks = head.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]');
  existingLinks.forEach((link) => link.remove());
  const existingMeta = head.querySelectorAll('meta[name^="apple-mobile-web-app"]');
  existingMeta.forEach((meta) => meta.remove());
  if (brandedConfig.favicon) {
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = 'image/x-icon';
    faviconLink.href = brandedConfig.favicon;
    head.appendChild(faviconLink);
  }
  if (brandedConfig.favicon32) {
    const favicon32Link = document.createElement('link');
    favicon32Link.rel = 'icon';
    favicon32Link.type = 'image/png';
    favicon32Link.setAttribute('sizes', '32x32');
    favicon32Link.href = brandedConfig.favicon32;
    head.appendChild(favicon32Link);
  }
  if (brandedConfig.favicon16) {
    const favicon16Link = document.createElement('link');
    favicon16Link.rel = 'icon';
    favicon16Link.type = 'image/png';
    favicon16Link.setAttribute('sizes', '16x16');
    favicon16Link.href = brandedConfig.favicon16;
    head.appendChild(favicon16Link);
  }
  if (brandedConfig.appleTouchIcon) {
    const appleTouchIconLink = document.createElement('link');
    appleTouchIconLink.rel = 'apple-touch-icon';
    appleTouchIconLink.setAttribute('sizes', '180x180');
    appleTouchIconLink.href = brandedConfig.appleTouchIcon;
    head.appendChild(appleTouchIconLink);
    const appleCapableMeta = document.createElement('meta');
    appleCapableMeta.name = 'apple-mobile-web-app-capable';
    appleCapableMeta.content = 'yes';
    head.appendChild(appleCapableMeta);
    const appleStatusBarMeta = document.createElement('meta');
    appleStatusBarMeta.name = 'apple-mobile-web-app-status-bar-style';
    appleStatusBarMeta.content = 'default';
    head.appendChild(appleStatusBarMeta);
  }
  if (brandedConfig.androidChrome192) {
    const androidChrome192Link = document.createElement('link');
    androidChrome192Link.rel = 'icon';
    androidChrome192Link.type = 'image/png';
    androidChrome192Link.setAttribute('sizes', '192x192');
    androidChrome192Link.href = brandedConfig.androidChrome192;
    head.appendChild(androidChrome192Link);
  }
  if (brandedConfig.androidChrome512) {
    const androidChrome512Link = document.createElement('link');
    androidChrome512Link.rel = 'icon';
    androidChrome512Link.type = 'image/png';
    androidChrome512Link.setAttribute('sizes', '512x512');
    androidChrome512Link.href = brandedConfig.androidChrome512;
    head.appendChild(androidChrome512Link);
  }
};

const generateWebManifestData = () => {
  const manifest = {
    name: brandedConfig.productName,
    short_name: brandedConfig.shortProductName,
    // theme_color: brandedConfig.primaryColor,
    // background_color: brandedConfig.primaryColor,
    theme_color: '#000000',
    background_color: '#ffffff',
    display: 'standalone',
    icons: [
      {
        src: brandedConfig.androidChrome192,
        sizes: '192x192',
        type: 'image/png',
      },
      {
        src: brandedConfig.androidChrome512,
        sizes: '512x512',
        type: 'image/png',
      },
    ],
  };
  return manifest;
};

export const setupWebManifest = () => {
  const manifestString = JSON.stringify(generateWebManifestData());
  const blob = new Blob([manifestString], { type: 'application/json' });
  const manifestURL = URL.createObjectURL(blob);

  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  link.href = manifestURL;
};
